import { ConsentBanner } from "@moznsystems/mozn-ui";
import {
	type CategoryDecision,
	useConsent,
} from "@moznsystems/mozn-ui/consent";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PRIVACY_POLICY_URL } from "@/constants/constants";
import {
	consentEngine,
	getActiveCategoryIds,
	requiresConsent,
} from "@/services/consent";
import type { ConsentCategoryId } from "@/services/consent/config";
import { useCookieConsentStoreWithShallow } from "@/store/cookieConsentStore";
import { CookiePreferencesModal } from "./components/cookie-preferences-modal";

/**
 * Asks for consent on the first visit (and again once a decision expires or
 * the consent revision is bumped), and hosts the preferences modal the
 * account menu reopens so consent can be withdrawn as easily as it was given.
 */
const CookieConsent: FC = () => {
	const { t } = useTranslation("layout");
	const { decision, isAccepted, acceptAll, rejectNonEssential, setCustom } =
		useConsent(consentEngine);
	const { isPreferencesOpen, openPreferences, closePreferences } =
		useCookieConsentStoreWithShallow([
			"isPreferencesOpen",
			"openPreferences",
			"closePreferences",
		]);
	const categoryIds = useMemo(
		() => getActiveCategoryIds() as ConsentCategoryId[],
		[],
	);

	const handleAcceptAll = useCallback(() => {
		acceptAll();
		closePreferences();
	}, [acceptAll, closePreferences]);

	const handleRejectNonEssential = useCallback(() => {
		rejectNonEssential();
		closePreferences();
	}, [rejectNonEssential, closePreferences]);

	const handleSave = useCallback(
		(draft: CategoryDecision) => {
			setCustom(draft);
			closePreferences();
		},
		[setCustom, closePreferences],
	);

	if (!requiresConsent()) return null;

	if (isPreferencesOpen) {
		return (
			<CookiePreferencesModal
				categoryIds={categoryIds}
				isAccepted={isAccepted}
				onAcceptAll={handleAcceptAll}
				onRejectNonEssential={handleRejectNonEssential}
				onSave={handleSave}
				onClose={closePreferences}
			/>
		);
	}

	if (decision !== null) return null;

	return (
		<ConsentBanner
			ariaLabel={t("cookie_consent.banner.aria_label")}
			description={
				<>
					{t("cookie_consent.banner.description")}
					{PRIVACY_POLICY_URL && (
						<>
							{" "}
							<a
								href={PRIVACY_POLICY_URL}
								target="_blank"
								rel="noopener noreferrer"
								className="underline"
							>
								{t("cookie_consent.banner.privacy_policy")}
							</a>
						</>
					)}
				</>
			}
			acceptAllLabel={t("cookie_consent.banner.accept_all")}
			managePreferencesLabel={t("cookie_consent.banner.manage_preferences")}
			onAcceptAll={acceptAll}
			onManagePreferences={openPreferences}
			testId="cookie-consent-banner"
		/>
	);
};

export { CookieConsent };
